import React, { useEffect, useState } from "react";
import {
  fetchGroupsForReview,
  updateReviewStatus,
} from "../../../services/firebaseService/endPoints/group";
import { Modal, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import SuperAdminContainer from "../SuperAdminContainer";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import logger from "../../../utils/logger";
import { ChantSelect } from "../../../CommonComponents";
import { countries } from "countries-list";

let countryCodes = {};
Object.keys(countries).forEach((key) => {
  countryCodes[key] = countries[key].name;
});

const REVIEW_STATUS_OPTIONS = [
  {
    value: "review",
    label: "Needs Review",
  },

  {
    value: "live",
    label: "Live",
  },
  {
    value: "notPublished",
    label: "Not Published",
  },
];

const REVIEW_STATUS_SORT_ORDER = ["review", "notPublished", "live"];

const Directory = () => {
  const [tableData, setTableData] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all");
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [showUnPublishConfirmModal, setShowUnPublishConfirmModal] =
    useState(false);
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);

  const handleFilterChange = (value) => {
    setReviewStatusFilter(value);
    if (value === "all") {
      setFilteredTableData(tableData);
    } else {
      setFilteredTableData(
        tableData.filter((data) => data.directoryStatus === value)
      );
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setReviewStatusFilter("all");
      const data = await fetchGroupsForReview();

      data.length > 0 &&
        data.sort((a, b) => {
          return (
            REVIEW_STATUS_SORT_ORDER.indexOf(a.directoryStatus) -
            REVIEW_STATUS_SORT_ORDER.indexOf(b.directoryStatus)
          );
        });

      setTableData(data);
      setFilteredTableData(data);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeReviewStatus = async (
    groupId,
    status,
    isUnpublishConirmed = false
  ) => {
    setSelectedGroupId(groupId);
    try {
      if (status === "notPublished" && !isUnpublishConirmed) {
        setShowUnPublishConfirmModal(true);
      } else {
        setUpdateStatusLoading(true);
        await updateReviewStatus(groupId, status);
        fetchData();
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setUpdateStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      title: "",
      width: 150,
      render: (data) => (
        <div
          className="d-flex justify-content-center cursor-pointer"
          style={{ gap: 20 }}
        >
          <Tooltip title="View">
            <a
              href={`https://directory.chant.fan/details/${data.id}`}
              target="#"
            >
              <i className="fa fa-search" aria-hidden="true"></i>
            </a>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Review Status",
      key: "reviewStatus",
      width: 200,
      render: (data) => {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {data?.needsReview && (
              <Tag color="orange" style={{ width: "fit-content" }}>
                New Submission
              </Tag>
            )}
            <ChantSelect
              loading={updateStatusLoading && selectedGroupId === data.id}
              disabled={updateStatusLoading && selectedGroupId === data.id}
              placeholder="Select Review Status"
              value={data?.directoryStatus}
              options={REVIEW_STATUS_OPTIONS}
              onChange={(value) => {
                handleChangeReviewStatus(data.id, value);
              }}
            />
          </div>
        );
      },
      className: "header-white",
    },
    {
      title: "Group",
      key: "groupName",
      dataIndex: "groupName",
      className: "header-white",
      render: (status) => <span>{status}</span>,
    },
    {
      title: "Club",
      key: "clubName",
      dataIndex: "clubName",
      className: "header-white",
      render: (clubName) => <span>{clubName}</span>,
    },
    {
      title: "League",
      key: "leagueName",
      dataIndex: "leagueName",
      className: "header-white",
      render: (leagueName) => <span>{leagueName}</span>,
    },
    {
      title: "City",
      key: "city",
      dataIndex: "city",
      className: "header-white",
      render: (city) => <span>{city}</span>,
    },
    {
      title: "Country",
      key: "country",
      dataIndex: "country",
      className: "header-white",
      render: (country) => (
        <span>{countryCodes[country] || country || ""}</span>
      ),
    },
    {
      title: "Size",
      key: "size",
      className: "header-white",
      render: (rec) => (
        <span>{((rec.websiteData || {}).general || {}).size}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      className: "header-white",
      render: (status) => <span>{status}</span>,
    },
    {
      title: "Account Creator",
      key: "accountCreator",
      className: "header-white",
      render: (rec) => <span>{(rec.creatorData || {}).name}</span>,
    },
    {
      title: "Email",
      key: "accountCreatorEmail",
      className: "header-white",
      render: (rec) => (
        <a href={`mailto:${(rec.creatorData || {}).email}`}>
          {(rec.creatorData || {}).email}
        </a>
      ),
    },
    {
      title: "Created",
      key: "createdOn",
      dataIndex: "createdOn",
      className: "header-white",
      render: (createdOn) => (
        <span>
          {createdOn ? dayjs.unix(createdOn).format("MMM DD YYYY") : ""}
        </span>
      ),
    },
    {
      title: "Last Updated",
      key: "updatedOn",
      className: "header-white",
      render: (rec) => {
        return (
          <span>
            {rec?.websiteData?.updatedOn
              ? dayjs.unix(rec?.websiteData?.updatedOn).format("MMM DD YYYY")
              : ""}
          </span>
        );
      },
    },
    {
      title: "Website",
      key: "webiste",
      className: "header-white",
      render: (rec) => (
        <span>{((rec.websiteData || {}).contact || {}).website}</span>
      ),
    },
    {
      title: "Instagram",
      key: "insta",
      className: "header-white",
      render: (rec) => (
        <span>{((rec.websiteData || {}).social || {}).insta}</span>
      ),
    },
  ];

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Directory"
        elementCount={filteredTableData?.length}
        actions={
          <div className="d-flex gap-4 align-items-center">
            <ChantSelect
              style={{ width: "150px" }}
              options={[
                { label: "All", value: "all" },
                { label: "Needs Review", value: "review" },
                { label: "Live", value: "live" },
                { label: "Not Published", value: "notPublished" },
              ]}
              value={reviewStatusFilter}
              onChange={(value) => {
                handleFilterChange(value);
              }}
            />
            <ChantButton onClick={fetchData}>Refresh</ChantButton>
          </div>
        }
      />

      <Table
        bordered
        columns={columns}
        dataSource={filteredTableData}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          position: ["bottomLeft"],
        }}
      />

      <Modal
        title="Confirm Unpublish"
        open={showUnPublishConfirmModal}
        footer={[]}
        width={500}
        centered
      >
        <div>
          <p>Are you sure you want to unpublish this group?</p>
        </div>
        <div className="d-flex gap-2 justify-content-end mt-4">
          <ChantButton
            onClick={() => {
              setShowUnPublishConfirmModal(false);
              setSelectedGroupId(null);
            }}
            type="secondary"
          >
            Cancel
          </ChantButton>
          <ChantButton
            onClick={() => {
              handleChangeReviewStatus(selectedGroupId, "notPublished", true);
              setShowUnPublishConfirmModal(false);
            }}
            type="primary"
          >
            Unpublish
          </ChantButton>
        </div>
      </Modal>
    </SuperAdminContainer>
  );
};

export default Directory;
